import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import authService from 'services/authService';
import { useAuth } from 'contexts/AuthContext';

export default function ForcePasswordChange({ onChanged }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { logout } = useAuth();
  const currentUser = authService.getCurrentUser();

  const handleLogout = () => {
    logout();
    authService.clearUserData();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    window.location.href = '/auth/sign-in';
  };

  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      toast({
        title: 'La nueva contraseña debe tener al menos 6 caracteres',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Las contraseñas no coinciden',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (newPassword === currentPassword) {
      toast({
        title: 'La nueva contraseña debe ser distinta de la temporal',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await authService.changePassword(currentPassword, newPassword);
      toast({
        title: 'Contraseña actualizada',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onChanged();
    } catch (error) {
      toast({
        title: 'No se pudo cambiar la contraseña',
        description: error.response?.data?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50" px={4}>
      <Box bg="white" borderRadius="lg" boxShadow="md" p={8} w="100%" maxW="440px">
        <VStack align="stretch" spacing={4}>
          <Heading size="md">Cambiar contraseña</Heading>
          <Text fontSize="sm" color="gray.600">
            {currentUser?.name ? `${currentUser.name}, tu ` : 'Tu '}
            contraseña fue reseteada. Tenés que elegir una nueva para continuar.
          </Text>
          <FormControl isRequired>
            <FormLabel>Contraseña temporal</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
              <InputRightElement>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Nueva contraseña</FormLabel>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Confirmar nueva contraseña</FormLabel>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </FormControl>
          <Button colorScheme="gray" onClick={handleSubmit} isLoading={loading}>
            Guardar y continuar
          </Button>
          <Button variant="ghost" onClick={handleLogout}>
            Cerrar sesión
          </Button>
        </VStack>
      </Box>
    </Flex>
  );
}
